/**
 * Emotes: confetti and the other full-window animations the party popper
 * sends everyone with a project open (see server/src/confetti.ts), each with a
 * banner saying who sent it.
 */
import { rainConfetti } from "./confetti";
import balloons from "./emotes/balloons";
import caps from "./emotes/caps";
import fire from "./emotes/fire";
import fireworks from "./emotes/fireworks";
import gravity from "./emotes/gravity";
import leaves from "./emotes/leaves";
import night from "./emotes/night";
import storm from "./emotes/storm";
import "./emotes.css";

export interface Emote {
  kind: string;
  name: string;
  emoji: string;
  /** Finishes the banner: "**Ben** …". */
  did: string;
  /** Plays it once, resolving when it's gone. */
  play: () => Promise<void>;
}

/** In the picker's order, three to a row. Keep the kinds in step with EMOTES in server/src/confetti.ts. */
export const EMOTES: Emote[] = [
  { kind: "confetti", name: "Confetti", emoji: "🎉", did: "sent everyone confetti", play: () => rainConfetti(260) },
  { kind: "fireworks", name: "Fireworks", emoji: "🎆", did: "set off fireworks", play: fireworks },
  { kind: "balloons", name: "Balloons", emoji: "🎈", did: "sent everyone balloons. Click one to pop it", play: balloons },
  { kind: "caps", name: "Grad caps", emoji: "🎓", did: "threw their caps in the air", play: caps },
  { kind: "leaves", name: "Leaf gust", emoji: "🍃", did: "sent a gust of leaves", play: leaves },
  { kind: "night", name: "Night sky", emoji: "🌌", did: "brought out the stars", play: night },
  { kind: "storm", name: "Thunderstorm", emoji: "⛈️", did: "brought a thunderstorm", play: storm },
  { kind: "fire", name: "Deadline fire", emoji: "🔥", did: "set everything on fire", play: fire },
  { kind: "gravity", name: "Gravity", emoji: "🍎", did: "switched gravity on", play: gravity },
];

interface Playing {
  /** Who sent it, in order; null is you. */
  senders: Array<string | null>;
  banner: HTMLElement;
}

/** What's playing in this tab, by kind. */
const playing = new Map<string, Playing>();
/** Emotes already shown in this tab. */
const seen = new Set<string>();

/** "🎆 **Ben** set off fireworks", "🎆 **You** and **Ben** …", "🎆 **Ben**, **Alex** and **Sam** …". */
function fillBanner(p: Playing, emote: Emote) {
  const names = p.senders.map((s) => s ?? "You");
  p.banner.replaceChildren(`${emote.emoji} `);
  names.forEach((name, i) => {
    if (i) p.banner.append(i === names.length - 1 ? " and " : ", ");
    p.banner.append(Object.assign(document.createElement("strong"), { textContent: name }));
  });
  p.banner.append(` ${emote.did}`);
}

/** Banners stack under the top bar, one per emote that's playing. */
function addBanner(): HTMLElement {
  let stack = document.querySelector(".emote-banners");
  if (!stack) {
    stack = Object.assign(document.createElement("div"), { className: "emote-banners" });
    stack.setAttribute("role", "status");
    document.body.appendChild(stack);
  }
  return stack.appendChild(Object.assign(document.createElement("div"), { className: "confetti-banner" }));
}

function removeBanner(el: HTMLElement) {
  el.classList.add("leaving");
  setTimeout(() => {
    const stack = el.parentElement;
    el.remove();
    if (stack && !stack.children.length) stack.remove();
  }, 400);
}

/** An emote someone sent everyone, as the server broadcasts it. Tabs from before there was a choice send no kind. */
export function receiveEmote(event: Record<string, unknown>, me: number) {
  const from = event.from as { id: number; name: string };
  showEmote(String(event.kind ?? "confetti"), String(event.id), from.id === me ? null : from.name);
}

/**
 * Play an emote from someone (null: you). The sender's tab hears about it
 * twice, from its own request and over its socket, so each one plays once.
 * Confetti that arrives while some is falling adds to the storm; the others
 * don't double up, they just add the sender to the banner.
 */
export function showEmote(kind: string, id: string, from: string | null) {
  if (seen.has(id)) return;
  seen.add(id);
  const emote = EMOTES.find((e) => e.kind === kind) ?? EMOTES[0];
  const now = playing.get(emote.kind);
  if (now) {
    if (!now.senders.includes(from)) now.senders.push(from);
    fillBanner(now, emote);
    if (emote.kind === "confetti") void emote.play();
    return;
  }
  const p: Playing = { senders: [from], banner: addBanner() };
  playing.set(emote.kind, p);
  fillBanner(p, emote);
  emote
    .play()
    .catch(() => {})
    .finally(() => {
      playing.delete(emote.kind);
      removeBanner(p.banner);
    });
}
